/**
 * Module dependencies.
 */

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from 'app/components/ui/alert-dialog';
import { Button } from 'app/components/ui/button';
import { Database, Loader2, Trash2 } from 'lucide-react';
import { Navigate } from 'react-router-dom';
import { Title } from 'app/components/atoms/title/title';
import { appRoutes } from 'app/routes';
import { useMe } from 'app/hooks/requests/use-me';
import { useRollbackCryptoGest, useSeedCryptoGest } from 'app/hooks/requests/use-admin-development';
import { useToast } from 'app/components/providers/toast-provider/toast-provider';
import { useTranslation } from 'react-i18next';
import React, { useState } from 'react';

/**
 * Export `AdministrationDevelopment` component.
 *
 * Admin-only dev helpers. Currently: seed / rollback the Crypto Gest manual
 * wallets for the logged-in user, so the feature has data to work against.
 */

export function AdministrationDevelopment(): JSX.Element {
  const [translate] = useTranslation();
  const { success, error } = useToast();
  const { data: me } = useMe();
  const seed = useSeedCryptoGest();
  const rollback = useRollbackCryptoGest();
  const [confirmRollback, setConfirmRollback] = useState(false);

  if (me && !me.isAdmin) {
    return <Navigate replace to={appRoutes.dashboard.home.base} />;
  }

  const onSeed = async () => {
    try {
      const result = await seed.mutateAsync();

      success({
        message: translate('administration.development.seeded', {
          entries: result?.entries ?? 0,
          wallets: result?.wallets ?? 0
        })
      });
    } catch {
      error({ message: translate('administration.development.seedFailed') });
    }
  };

  const onRollback = async () => {
    setConfirmRollback(false);

    try {
      await rollback.mutateAsync();

      success({ message: translate('administration.development.rolledBack') });
    } catch {
      error({ message: translate('administration.development.rollbackFailed') });
    }
  };

  return (
    <div className={'space-y-5'}>
      <div>
        <Title>{translate('administration.development.title')}</Title>

        <p className={'mt-1 text-sm text-muted-foreground'}>{translate('administration.development.description')}</p>
      </div>

      <div className={'rounded-xl border border-border bg-card p-5 shadow-sm'}>
        <div className={'flex items-start gap-3'}>
          <div className={'grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary'}>
            <Database className={'h-5 w-5'} />
          </div>

          <div className={'min-w-0 flex-1'}>
            <h3 className={'text-sm font-semibold text-foreground'}>
              {translate('administration.development.cryptoGest.title')}
            </h3>

            <p className={'mt-1 text-sm text-muted-foreground'}>
              {translate('administration.development.cryptoGest.description')}
            </p>

            {me?.email && (
              <p className={'mt-2 text-xs text-muted-foreground'}>
                {translate('administration.development.forUser', { email: me.email })}
              </p>
            )}

            <div className={'mt-4 flex flex-wrap gap-3'}>
              <Button disabled={seed.isLoading || rollback.isLoading} onClick={onSeed}>
                {seed.isLoading ? (
                  <Loader2 className={'mr-2 h-4 w-4 animate-spin'} />
                ) : (
                  <Database className={'mr-2 h-4 w-4'} />
                )}

                {translate(seed.isLoading ? 'administration.development.seeding' : 'administration.development.seed')}
              </Button>

              <Button
                disabled={seed.isLoading || rollback.isLoading}
                onClick={() => setConfirmRollback(true)}
                variant={'outline'}
              >
                {rollback.isLoading ? (
                  <Loader2 className={'mr-2 h-4 w-4 animate-spin'} />
                ) : (
                  <Trash2 className={'mr-2 h-4 w-4'} />
                )}

                {translate(
                  rollback.isLoading ? 'administration.development.rollingBack' : 'administration.development.rollback'
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <AlertDialog onOpenChange={setConfirmRollback} open={confirmRollback}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{translate('administration.development.rollbackConfirm.title')}</AlertDialogTitle>

            <AlertDialogDescription>
              {translate('administration.development.rollbackConfirm.description')}
            </AlertDialogDescription>
          </AlertDialogHeader>

          <AlertDialogFooter>
            <AlertDialogCancel>{translate('common.actions.cancel')}</AlertDialogCancel>

            <AlertDialogAction onClick={onRollback}>
              {translate('administration.development.rollback')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
